// React Query hooks for a single WB ticket — Phase C9 Group 2.
//
// Pairs with:
//   GET  /api/v1/wb/tickets/:enc_id          (ticket detail)
//   POST /api/v1/wb/tickets/:enc_id/reissue  (clone voided -> new draft)
//
// DTOs mirror server/src/modules/wb/tickets/types.ts (TicketDetailDto et al)
// per WEIGHBRIDGE_SERVER_CONTRACT.md §C9. We deliberately re-declare the
// shared enums (TicketStatus / TicketDirection / WeighingMode) locally so
// this file can be authored independently of Group 1's `useWbTickets.ts`;
// TypeScript's structural typing keeps the two unified at the boundary.
//
// Reissue is the ONLY mutation wired in C9 — void / second-weigh land in
// C11. The reissue button is reachable only from the read-only detail
// page (status === 'voided'), so it belongs here rather than in the list
// hook.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- Shared enums (re-declared from useWbTickets.ts — see header note) -----

export type TicketStatus = 'draft' | 'open' | 'completed' | 'voided';

export type TicketDirection = 'inwards' | 'outwards';

export type WeighingMode =
  | 'two_pass'
  | 'stored_tare'
  | 'manual_tare'
  | 'units_only';

// -- Reference shapes -------------------------------------------------------

export interface TicketRef {
  readonly enc_id: string;
  readonly name: string;
}

export interface TicketVehicleRef {
  readonly enc_id: string;
  readonly registration: string;
}

export interface TicketProductRef {
  readonly enc_id: string;
  readonly name: string;
  readonly code: string;
}

export interface TicketCameraRef {
  readonly enc_id: string;
  readonly name: string;
}

// -- Line / weight-event / snapshot DTOs ------------------------------------

export type TicketLineMode = 'weight' | 'unit' | 'custom';

export type UnitPriceBasis =
  | 'contract'
  | 'price_band'
  | 'general_public'
  | 'manual_override';

export interface TicketLineDto {
  /**
   * Stable line identity (wb_ticket_lines.uid). Persisted server-side since
   * migration 185; null for lines created before that migration. Present on
   * optimistic lines projected from the editor too (so the right-rail pricing
   * summary can key resolvedPrices by it).
   */
  readonly uid?: string | null;
  readonly id: number;
  /** Encrypted line reference — send in set-active-ticket so a Kiosk capture
   *  links to this specific line (gross_image_id/tare_image_id/photo_image_id)
   *  instead of only the ticket as a whole. */
  readonly line_enc_id: string;
  readonly line_no: number;
  readonly product: TicketProductRef | null;
  readonly product_name_snapshot: string | null;
  readonly product_code_snapshot: string | null;
  readonly mode: TicketLineMode;
  readonly quantity: number;
  readonly gross_weight_kg: number | null;
  readonly tare_weight_kg: number | null;
  readonly net_weight_kg: number | null;
  readonly unit_price: number;
  readonly unit_price_basis: UnitPriceBasis | null;
  readonly price_basis_ref_id: string | null;
  readonly override_reason: string | null;
  readonly ewc_code_snapshot: string | null;
  readonly vat_rate_id: string | null;
  readonly vat_rate_snapshot_pct: number | null;
  readonly vat_amount: number;
  readonly subtotal: number;
  readonly line_total: number;
  /** Storage keys for this line's captured photo(s) — resolve via
   *  GET /files/presigned, same as TicketSnapshotDto.image_url. Null when no
   *  capture has linked to that slot yet. */
  readonly gross_image_url: string | null;
  readonly tare_image_url: string | null;
  readonly photo_image_url: string | null;
}

export type WeightType = 'gross' | 'tare' | 'verify';

export type WeightMethod = 'hardware' | 'manual' | 'stored';

export interface WeightEventDto {
  readonly id: number;
  readonly ticket_line_id: number | null;
  readonly weight_type: WeightType;
  readonly weight_kg: number;
  readonly method: WeightMethod;
  readonly weighbridge: TicketRef | null;
  readonly captured_at: string;
  readonly operator_id: number | null;
  readonly is_stable: boolean;
  readonly attempt_no: number;
}

export type SnapshotLabel =
  | 'gross'
  | 'tare'
  | 'material'
  | 'plate'
  | 'driver'
  | 'other';

export interface TicketSnapshotDto {
  readonly id: number;
  readonly camera: TicketCameraRef | null;
  readonly weight_event_id: number | null;
  readonly captured_at: string;
  /** MinIO storage key — resolve via GET /files/presigned. */
  readonly image_url: string;
  /** MinIO storage key for the thumbnail when present (else null). */
  readonly image_thumb_url: string | null;
  readonly label: SnapshotLabel;
  readonly anpr_plate_text: string | null;
  readonly anpr_confidence: number | null;
}

// -- Detail DTO -------------------------------------------------------------
//
// Extends the list-shaped TicketSummaryDto with the deep fields the
// detail endpoint adds (server: TicketDetailDto in modules/wb/tickets).

export type PaymentMethod = 'cash' | 'card' | 'account' | 'mixed';

export interface TicketDetailDto {
  // -- Summary surface (mirrors TicketSummaryDto in useWbTickets.ts) ------
  readonly enc_id: string;
  readonly ticket_number: string | null;
  readonly direction: TicketDirection;
  readonly status: TicketStatus;
  readonly weighing_mode: WeighingMode;
  readonly customer: TicketRef;
  /** False when the linked customer was quick-created at the kiosk and still
   *  lacks contact/address detail. Completion is blocked server-side while
   *  false; the workspace disables the complete button + shows a prompt.
   *  Optional on the client mirror so older fixtures default to "complete"
   *  (only an explicit `false` blocks completion). */
  readonly customer_is_complete?: boolean;
  readonly vehicle_reg_snapshot: string | null;
  readonly net_weight_kg: number | null;
  readonly total: number;
  readonly yard: TicketRef;
  readonly created_at: string;
  readonly completed_at: string | null;
  readonly notes_count: number;

  // -- Detail-only fields -------------------------------------------------
  readonly haulier: TicketRef | null;
  readonly vehicle: TicketVehicleRef | null;
  readonly driver: TicketRef | null;
  readonly weighbridge: TicketRef | null;

  readonly gross_weight_kg: number | null;
  readonly gross_weighed_at: string | null;
  readonly gross_method: 'hardware' | 'manual' | null;
  readonly tare_weight_kg: number | null;
  readonly tare_weighed_at: string | null;
  readonly tare_method: WeightMethod | null;

  readonly lines: ReadonlyArray<TicketLineDto>;
  readonly weight_events: ReadonlyArray<WeightEventDto>;
  readonly snapshots: ReadonlyArray<TicketSnapshotDto>;

  readonly notes: string | null;
  readonly internal_notes: string | null;

  readonly subtotal: number;
  readonly vat_total: number;
  readonly payment_method: PaymentMethod | null;
  readonly paid_amount: number;
  readonly balance: number;

  readonly voided_at: string | null;
  readonly void_reason: string | null;

  // Linked scrap invoice raised from an account ticket. After a void the
  // status is 'cancelled' (the credit-note-on-void mirror). Null for cash
  // tickets and drafts that never raised an invoice.
  readonly invoice: TicketInvoiceRef | null;
}

export interface TicketInvoiceRef {
  readonly enc_id: string;
  readonly invoice_number: string;
  readonly status: string;
}

interface TicketDetailResponse {
  ticket: TicketDetailDto;
}

// -- Queries ----------------------------------------------------------------

export function useWbTicket(encId: string | undefined) {
  const enabled = !!encId && encId !== 'new';
  return useQuery<TicketDetailResponse>({
    queryKey: queryKeys.wb.ticket(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useWbTicket called without enc_id');
      const res = await apiClient.get<TicketDetailResponse>(
        endpoints.wb.ticket(encId),
      );
      return res.data;
    },
    enabled,
    staleTime: 15_000,
  });
}

// -- Mutations --------------------------------------------------------------

/**
 * Reissue a voided ticket. Server returns a fresh `{ ticket }` for the new
 * draft. We invalidate the broad `['wb', 'tickets']` prefix so every list
 * cache picks up the new draft, plus the single-ticket cache for the
 * original (its detail page is still navigable and now shows a fresh
 * "reissued_to" reference in the future — for now we just want the void
 * banner to stay accurate).
 */
export function useReissueTicket(encId: string) {
  const qc = useQueryClient();
  return useMutation<TicketDetailResponse, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.post<TicketDetailResponse, Record<string, never>>(
        endpoints.wb.ticketReissue(encId),
        {},
      );
      return res.data;
    },
    onSuccess: () => {
      // Broad invalidate so every paginated ticket list (open / all / filtered)
      // refetches — the new draft must surface.
      void qc.invalidateQueries({ queryKey: ['wb', 'tickets'] });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.ticket(encId) });
    },
  });
}

/**
 * Explicit draft → open transition ("Mark as Open"). Weight capture no longer
 * advances status; this PATCH is the operator-driven move that makes a ticket
 * ready for billing/completion.
 */
export function useMarkTicketOpen(encId: string) {
  const qc = useQueryClient();
  return useMutation<TicketDetailResponse, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.patch<TicketDetailResponse, { status: 'open' }>(
        endpoints.wb.ticket(encId),
        { status: 'open' },
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['wb', 'tickets'] });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.ticket(encId) });
    },
  });
}
